"use client";

import React, { ReactNode } from "react";
import { CalendarDays, CalendarSync, CircleDollarSign, Headphones, ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { excursionTerms } from "@/lib/excursionTerms";
interface HelpTopic {
  title: string;
  desc: ReactNode;
  linkText: string;
  to: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const terms = excursionTerms.de;
const helpTopics: HelpTopic[] = [{
  title: "Transfer buchen",
  desc: <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
        Erfahren Sie, wie Sie anfragen: Jede Buchung wird vor Fahrtantritt von unserer Disposition geprüft und verbindlich bestätigt (keine Sofortbuchung).
      </p>,
  linkText: "Buchungsanleitung & Details",
  to: "/BookTransferDePage",
  icon: CalendarDays
}, {
  title: "Umbuchen oder Stornieren",
  desc: <div className="space-y-3 text-sm sm:text-base leading-relaxed">
        <p className="text-muted-foreground">
          Müssen Sie Flugzeit, Datum oder Zielhotel ändern?
        </p>
        <div className="space-y-1">
          <h3 className="font-semibold text-foreground text-sm">Transfers</h3>
          <p className="text-muted-foreground">
            Eine Stornierung ist bis spätestens 24 Stunden vor der vereinbarten Abhol- oder Treffzeit kostenlos. Bei einer späteren Stornierung fallen Stornogebühren in Höhe von 25 % des vereinbarten Gesamtbuchungspreises an.
          </p>
        </div>
        <div className="space-y-1">
          <h3 className="font-semibold text-foreground text-sm">Ausflüge</h3>
          <p className="text-muted-foreground break-words">
            {terms.cancellation}{" "}
            {terms.weatherRefund}
          </p>
        </div>
      </div>,
  linkText: "Stornierungsrichtlinien",
  to: "/TermsConditionsDEPage",
  icon: CalendarSync
}, {
  title: "Zahlung & Preise",
  desc: <div className="space-y-3 text-sm sm:text-base leading-relaxed">
        <div className="space-y-1">
          <h3 className="font-semibold text-foreground text-sm">Transfers</h3>
          <p className="text-muted-foreground">
            Feste Preise pro Fahrzeug, Barzahlung bei Ankunft am Hotel in einer zuvor mit unserem Team abgestimmten Währung.
          </p>
        </div>
        <div className="space-y-1">
          <h3 className="font-semibold text-foreground text-sm">Ausflüge</h3>
          <p className="text-muted-foreground break-words">
            {terms.paymentOptions}
          </p>
        </div>
      </div>,
  linkText: "Zahlungsfragen & Währungen",
  to: "/TaxiServiceDEPage",
  icon: CircleDollarSign
}, {
  title: "Support kontaktieren",
  desc: <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
        Erreichen Sie unsere Disponenten rund um die Uhr für Reiseunterstützung in Ägypten.
      </p>,
  linkText: "Kontaktmöglichkeiten",
  to: "/BookTransferDePage#talk-to-us-directly",
  icon: Headphones
}];
export default function HelpTopics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1,
    once: true
  });
  return <section id="how-can-we-help" ref={ref} data-nav="light" className="relative bg-background text-foreground py-16 md:py-20 lg:py-24 overflow-hidden border-b border-border">
      <div className="max-w-[1136px] mx-auto px-6 sm:px-8">
        {/* Header Block */}
        <div className={`max-w-2xl mx-auto text-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="inline-block text-xs uppercase tracking-wider font-semibold text-primary mb-3">
            Kundenservice
          </span>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default">
            Wie können wir helfen?
          </h1>

          {/* Signature Hairline Rule with Amber Dot */}
          <div className="flex items-center justify-center gap-2 mt-6 mb-2">
            <div className="h-px w-20 bg-border" />
            <div className="size-1.5 rounded-full bg-primary" />
            <div className="h-px w-20 bg-border" />
          </div>
        </div>

        {/* 2x2 Topics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 md:mt-14">
          {helpTopics.map((topic, i) => {
          const Icon = topic.icon;
          return <div key={topic.title} data-index={i} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: isVisible ? `${60 * (i + 1)}ms` : "0ms"
          }}>
                <Card className="h-full border border-border bg-card hover:border-primary hover:shadow-md transition-all duration-200 flex flex-col justify-between p-6 sm:p-7">
                  <div>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="size-10 rounded-md flex items-center justify-center bg-primary/10 border border-primary/25 text-primary shrink-0">
                        <Icon className="size-5" />
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-semibold text-card-foreground">
                        {topic.title}
                      </CardTitle>
                    </div>

                    <div className="mt-2">
                      {topic.desc}
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-border/60">
                    <Link to={topic.to} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors group">
                      <span>{topic.linkText}</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </Card>
              </div>;
        })}
        </div>

        {/* Bottom WhatsApp Inquiry Anchor */}
        <div className={`mt-12 md:mt-16 text-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: isVisible ? "320ms" : "0ms"
      }}>
          <div className="inline-flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-md bg-secondary border border-border/80 max-w-xl mx-auto">
            <span className="text-sm font-medium text-secondary-foreground text-center sm:text-left">
              Nicht gefunden, wonach Sie suchen?
            </span>
            <Button asChild className="bg-[#27BA2A] text-white hover:bg-[#22a025] h-10 px-5 text-sm font-medium shadow-xs">
              <Link to="https://wa.me/201010634627" newTab className="inline-flex items-center gap-2 text-white">
                <MessageCircle className="size-4" />
                <span>Mit Support auf WhatsApp chatten</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}